import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { ROUTES } from '@/constants';
import Homepage from '@pages/Homepage';
import ForgotPassword from '@pages/ForgotPassword';
import ResetPassword from '@pages/ResetPassword';
import AuthCallback from '@pages/AuthCallback';
import RegisterCallback from '@pages/RegisterCallback';
import ProfileSetup from '@pages/ProfileSetup';
import Onboarding from '@pages/Onboarding';
import TeamRegistration from '@pages/TeamRegistration';
import MFAEnroll from '@pages/MFAEnroll';
import MFAVerify from '@pages/MFAVerify';
import EmailConfirmation from '@pages/EmailConfirmation';
import Dashboard from '@pages/Dashboard';
import ParsePilot from '@pages/ParsePilot';
import TrackView from '@/features/track/view/main';
import TrackFlightView from '@/features/track/view/TrackFlightView';
import { Clients, AddClient, ClientDetail } from '@/features/clients';
import { FinancialsPage, FinancialReports } from '@/features/financials';
import SearchAircraft from '@pages/SearchAircraft';
import AircraftDetail from '@pages/AircraftDetail';
import { BidsList, BidDetail } from '@/features/bids';
import { GenerateQuote, QuotesList, QuotePreviewPage } from '@/features/quotes';
// Import BookingConfirmed directly to avoid loading Stripe from barrel export
import BookingConfirmed from '@/features/checkout/pages/BookingConfirmed';
import WireTransferConfirmed from '@/features/checkout/pages/WireTransferConfirmed';
import MessageList from '@pages/MessageList';
import { Tails } from '@/features/tails';
import { OperatorProfile } from '@/features/operators';
import { TripSheetPage, ClientTripSheet } from "@/features/trip-sheet";
import { TaxReports } from '@/features/reports';
import { BrokerManagementPage } from '@/features/broker-management';
import GenerateToken from '@pages/GenerateToken';
import { AccountSettings } from '@/features/account';
import EmailPreview from '@pages/EmailPreview';
import Welcome from '@pages/Welcome';
import ProtectedRoute from '@components/layouts/ProtectedRoute';
import LayoutProvider from '@components/layouts/LayoutProvider';
import { Toaster } from 'sonner';

// Lazy load CheckoutPage to prevent Stripe from loading on every page
const CheckoutPage = lazy(() => import('@/features/checkout/pages/CheckoutPage'));

function App() {
  return (
    <Router>
      <Toaster position="top-right" richColors />
      <div className="min-h-screen bg-white">
          <Routes>
          <Route path={ROUTES.HOME.path} element={<LayoutProvider 
              authLayoutProps={{
                title: "Welcome to TrustJet",
                subtitle: "Private skies, verified trust."
              }}
            >
              <Homepage />
            </LayoutProvider>} />
          <Route path={ROUTES.FORGOT_PASSWORD.path} element={
            <LayoutProvider 
              authLayoutProps={{
                title: "Forgot Password",
                subtitle: "Enter your email to reset your password"
              }}
            >
              <ForgotPassword />
            </LayoutProvider>
          } />
          <Route path={ROUTES.RESET_PASSWORD.path} element={
            <LayoutProvider 
              authLayoutProps={{
                title: "Reset Password",
                subtitle: "Enter your new password"
              }}
            >
              <ResetPassword />
            </LayoutProvider>
          } />
          <Route path={ROUTES.AUTH_CALLBACK.path} element={<AuthCallback />} />
          <Route path={ROUTES.REGISTER_CALLBACK.path} element={<RegisterCallback />} />
          <Route path={ROUTES.PROFILE_SETUP.path} element={
            <LayoutProvider
              authLayoutProps={{
                title: "Profile Setup",
                subtitle: "Complete your profile information"
              }}
            >
              <ProfileSetup />
            </LayoutProvider>
          } />
          <Route path={ROUTES.ONBOARDING.path} element={
            <LayoutProvider
              authLayoutProps={{
                title: "Onboarding",
                subtitle: "Complete your onboarding"
              }}
            >
              <Onboarding />
            </LayoutProvider>
          } />
          <Route path={ROUTES.REGISTER_TEAM.path} element={
            <LayoutProvider
              authLayoutProps={{
                title: "Team Registration",
                subtitle: "Complete your profile setup"
              }}
            >
              <TeamRegistration />
            </LayoutProvider>
          } />
          <Route path={ROUTES.PROFILE.path} element={
            <ProtectedRoute>
              <ProfileSetup isProfile="true" />
            </ProtectedRoute>
          } />
          <Route path={ROUTES.MFA_ENROLL.path} element={
            <LayoutProvider 
              authLayoutProps={{
                title: "Setup Two-Factor Authentication",
                subtitle: "Secure your account with 2FA"
              }}
            >
              <MFAEnroll />
            </LayoutProvider>
          } />
          <Route path={ROUTES.MFA_VERIFY.path} element={
            <LayoutProvider 
              authLayoutProps={{
                title: "Verify Authentication",
                subtitle: "Enter your verification code"
              }}
            >
              <MFAVerify />
            </LayoutProvider>
          } />
          <Route path={ROUTES.EMAIL_CONFIRMATION.path} element={
            <LayoutProvider 
              authLayoutProps={{
                title: "Email Confirmation",
                subtitle: "Please confirm your email address"
              }}
            >
              <EmailConfirmation />
            </LayoutProvider>
          } />
          <Route path={ROUTES.BIDS.path} element={
            <ProtectedRoute>
              <BidsList />
            </ProtectedRoute>
            } />
          <Route path={ROUTES.BID_DETAIL.path} element={
            <ProtectedRoute>
              <BidDetail />
            </ProtectedRoute>
            } />
          <Route path={ROUTES.QUOTES.path} element={
            <ProtectedRoute>
              <QuotesList />
            </ProtectedRoute>
            } />
          <Route path={ROUTES.GENERATE_QUOTE.path} element={
            <ProtectedRoute>
              <GenerateQuote />
            </ProtectedRoute>
            } />
          <Route path={ROUTES.DASHBOARD.path} element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path={ROUTES.PARSE_PILOT.path} element={
            <ProtectedRoute>
              <ParsePilot />
            </ProtectedRoute>
          } />
          <Route path={ROUTES.MESSAGES.path} element={
            <ProtectedRoute>
              <MessageList />
            </ProtectedRoute>
          } />
          <Route path={ROUTES.TRACK_FLIGHT.path} element={<TrackFlightView />} />
          <Route path={ROUTES.TRACK.path} element={
            <ProtectedRoute>
              <TrackView />
            </ProtectedRoute>
          } />
          <Route path={ROUTES.ADD_CLIENT.path} element={
            <ProtectedRoute>
              <AddClient />
            </ProtectedRoute>
          } />
          <Route path={ROUTES.EDIT_CLIENT.path} element={
            <ProtectedRoute>
              <AddClient />
            </ProtectedRoute>
          } />
          <Route path={ROUTES.CLIENT_DETAIL.path} element={
            <ProtectedRoute>
              <ClientDetail />
            </ProtectedRoute>
          } />
          <Route path={ROUTES.CLIENTS.path} element={
            <ProtectedRoute>
              <Clients />
            </ProtectedRoute>
          } />
          <Route path={ROUTES.FINANCIAL_REPORTS.path} element={
            <ProtectedRoute>
              <TaxReports />
            </ProtectedRoute>
          } />
          <Route path={ROUTES.FINANCIALS.path} element={
            <ProtectedRoute>
             <FinancialsPage /> 
            </ProtectedRoute>
          } />
          <Route path={ROUTES.SEARCH_AIRCRAFT.path} element={
            <ProtectedRoute>
              <SearchAircraft />
            </ProtectedRoute>
          } />
          <Route path={ROUTES.AIRCRAFT_DETAIL.path} element={
            <ProtectedRoute>
              <AircraftDetail />
            </ProtectedRoute>
          } />
          <Route path={ROUTES.CHECKOUT.path} element={
            <Suspense fallback={
              <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading checkout...</p>
                </div>
              </div>
            }>
              <CheckoutPage />
            </Suspense>
          } />
          <Route path={ROUTES.BOOKING_CONFIRMED.path} element={<BookingConfirmed />} />
          <Route path={ROUTES.WIRE_TRANSFER_CONFIRMED.path} element={<WireTransferConfirmed />} />
          <Route path="/email-preview" element={<EmailPreview />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path={ROUTES.TAILS.path} element={
            <ProtectedRoute>
              <Tails />
            </ProtectedRoute>
          } />
          <Route path={ROUTES.QUOTE_PREVIEW.path} element={<QuotePreviewPage />} />
          <Route path={ROUTES.CLIENT_TRIP_SHEET.path} element={<ClientTripSheet />} />
          <Route path={ROUTES.GENERATE_TOKEN.path} element={<GenerateToken />} />
          <Route path={ROUTES.OPERATOR_PROFILE.path} element={
            <ProtectedRoute>
              <OperatorProfile />
            </ProtectedRoute>
          } />
          <Route path={ROUTES.TRIP_SHEET.path} element={
            <ProtectedRoute>
              <TripSheetPage />
            </ProtectedRoute>
          } />
          <Route path={ROUTES.TAX_REPORTS.path} element={
            <ProtectedRoute>
              <TaxReports />
            </ProtectedRoute>
          } />
          <Route path={ROUTES.BROKER_MANAGEMENT.path} element={
            <ProtectedRoute>
              <BrokerManagementPage />
            </ProtectedRoute>
          } />
          <Route path={ROUTES.ACCOUNT_SETTINGS.path} element={
            <ProtectedRoute>
              <AccountSettings />
            </ProtectedRoute>
          } />
          </Routes>

        </div>
    </Router>
  );
}

export default App;
